import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Skull } from "lucide-react";
import Nav from "@/components/site/Nav";
import Hero from "@/components/site/Hero";
import AboutNarrative from "@/components/site/AboutNarrative";
import NightVision from "@/components/site/NightVision";
import GameInfo from "@/components/site/GameInfo";
import ChairShowcase from "@/components/site/ChairShowcase";
import Gallery from "@/components/site/Gallery";
import Reviews from "@/components/site/Reviews";
import Reviews2 from "@/components/site/Reviews2";
import SiteVideos from "@/components/site/SiteVideos";
import BookingSection from "@/components/site/BookingSection";
import Vouchers from "@/components/site/Vouchers";
import Faq from "@/components/site/Faq";
import Contact from "@/components/site/Contact";
import SocialLinks from "@/components/site/SocialLinks";
import Footer from "@/components/site/Footer";
import CookieBar from "@/components/site/CookieBar";
import ChatWidget from "@/components/site/ChatWidget";
import CustomBlocks from "@/components/site/CustomBlocks";
import { setTexts } from "@/data/texts";
import { setImages } from "@/data/images";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function HubertHome() {
  const [blocks, setBlocks] = useState([]);
  const [vis, setVis] = useState({});
  const [, setReady] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockMsg, setLockMsg] = useState("");
  const [gameRows, setGameRows] = useState(null);
  const [faqItems, setFaqItems] = useState(null);
  const [negReviews, setNegReviews] = useState(false);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    const onScroll = () => {
      document.body.classList.toggle("has-scrolled", window.scrollY > 120);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); document.body.classList.remove("has-scrolled"); };
  }, []);

  useEffect(() => {
    document.title = "HUBERT — Psychoactive Escapecore | GATE ESCAPE";
    let cid = localStorage.getItem("hubert_client_id");
    if (!cid) { cid = (crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random()}`); localStorage.setItem("hubert_client_id", cid); }
    api.post("/track/visit", { client_id: cid, path: window.location.pathname }).catch(() => {});
    api.get("/videos/public").then(({ data }) => setVideos(data)).catch(() => {});
    // maintenance-bypass token: platí jen pro aktuální relaci (sessionStorage), ne natrvalo
    localStorage.removeItem("hubert_bypass"); // úklid staré trvalé varianty
    const qs = new URLSearchParams(window.location.search);
    const urlBypass = qs.get("nahled");
    if (urlBypass) {
      sessionStorage.setItem("hubert_bypass", urlBypass);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const bypass = sessionStorage.getItem("hubert_bypass") || "";
    api.get("/settings/public", { params: { ...(bypass ? { bypass } : {}), _t: Date.now() } }).then(({ data }) => {
      setTexts(data.site_texts || {});
      setImages(data.site_images || {});
      setBlocks(data.custom_blocks || []);
      setVis(data.section_visibility || {});
      setNegReviews(!!data.reviews_neg_visible);
      setLocked(!!(data.site_locked_effective ?? data.site_locked));
      setLockMsg(data.site_locked_message || "");
      if (data.game_info_rows && data.game_info_rows.length) setGameRows(data.game_info_rows);
      if (data.faq_items && data.faq_items.length) setFaqItems(data.faq_items);
      if (data.favicon_url) {
        let link = document.querySelector("link[rel='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = data.favicon_url;
      }
      if (data.seo_title) document.title = data.seo_title;
      setMeta("description", data.seo_description);
      setMeta("keywords", data.seo_keywords);
      setMeta("og:title", data.seo_title || document.title, "property");
      setMeta("og:description", data.seo_description, "property");
      setMeta("og:type", "website", "property");
      if (data.seo_og_image) setMeta("og:image", data.seo_og_image, "property");
      setMeta("twitter:card", "summary_large_image");
      setReady(true);
    }).catch(() => {});
  }, []);

  const show = (key) => vis[key] !== false;
  const Z = (zone) => <CustomBlocks zone={zone} blocks={blocks} />;
  const V = (zone) => <SiteVideos zone={zone} videos={videos} />;

  if (locked) {
    return (
      <div className="grain-layer vignette min-h-screen bg-void text-neutral-200 flex items-center justify-center px-6" data-testid="site-locked">
        <div className="absolute inset-0 scanlines pointer-events-none" />
        <div className="relative z-10 text-center max-w-lg">
          <Skull className="w-14 h-14 text-red-600 mx-auto mb-6 glow-nv" />
          <h1 className="font-head text-3xl sm:text-4xl uppercase tracking-widest text-neutral-100 mb-4 vhs-glitch">HUBERT</h1>
          <p className="text-neutral-400 leading-relaxed whitespace-pre-line">{lockMsg || "Web je dočasně nedostupný."}</p>
          <a
            href="/admin"
            data-testid="locked-admin-login"
            className="inline-block mt-10 text-[11px] tracking-[0.3em] uppercase text-neutral-600 hover:text-nv transition-colors"
          >
            → Přihlášení do administrace
          </a>
        </div>
        {show("chatbot") && <ChatWidget />}
      </div>
    );
  }

  return (
    <div className="grain-layer vignette relative bg-void text-neutral-200 min-h-screen overflow-x-hidden">
      <Nav vis={vis} />
      <main>
        <Hero />
        {Z("after_hero")}
        {V("after_hero")}
        {show("about") && <AboutNarrative />}
        {Z("after_about")}
        {V("after_about")}
        {show("nightvision") && <NightVision />}
        {Z("after_nightvision")}
        {V("after_nightvision")}
        {show("gameinfo") && <GameInfo rows={gameRows} />}
        {show("chair") && <ChairShowcase />}
        {Z("after_gallery_top")}
        {show("gallery") && <Gallery />}
        {Z("after_gallery")}
        {V("after_gallery")}
        {show("reviews") && <Reviews />}
        {negReviews && <Reviews2 />}
        {V("reviews2")}
        {Z("after_reviews")}
        {V("after_reviews")}
        {show("booking") && <BookingSection />}
        {show("vouchers") && <Vouchers />}
        {show("faq") && <Faq items={faqItems} />}
        {Z("before_contact")}
        {V("before_contact")}
        {show("contact") && <Contact />}
        {show("social") && <SocialLinks />}
        {Z("before_footer")}
        {V("before_footer")}
      </main>
      <Footer />
      <CookieBar />
      {show("chatbot") && <ChatWidget />}
    </div>
  );
}
